import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw, Router } from 'vue-router';

import HomePage from '@/pages/HomePage.vue';
import { useAuthStore } from '@/features/auth/authStore';

declare module 'vue-router' {
  interface RouteMeta {
    title?: string;
    // Settings is admin-only (APPLICATION_SPEC item 2). The guard allows an
    // admin, and — as a first-run carve-out — the unconfigured operator who has
    // no identity yet, so they can still reach Settings to configure a token.
    requiresAdmin?: boolean;
  }
}

// The home page ships in the main bundle; the ported pages (browse, unit detail,
// settings, the /new builder, profile, the public per-user profile, join)
// lazy-load their real views, while the not-found catch-all lazy-loads StubPage.
// User management now lives under Settings, so `/admin` redirects there rather
// than resolving its own page.
export const routes: RouteRecordRaw[] = [
  { path: '/', name: 'home', component: HomePage },
  {
    path: '/units',
    name: 'browse',
    component: () => import('@/pages/BrowsePage.vue'),
    meta: { title: 'Browse' },
  },
  {
    path: '/units/:path(.+)',
    name: 'unit-detail',
    component: () => import('@/pages/UnitDetailPage.vue'),
    meta: { title: 'Unit' },
  },
  {
    path: '/new',
    name: 'new',
    component: () => import('@/pages/NewUnitPage.vue'),
    meta: { title: 'New unit' },
  },
  {
    path: '/edit/:path(.+)',
    name: 'edit-unit',
    component: () => import('@/pages/EditUnitPage.vue'),
    meta: { title: 'Edit unit' },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/pages/SettingsPage.vue'),
    meta: { title: 'Settings', requiresAdmin: true },
  },
  {
    path: '/signin',
    name: 'signin',
    component: () => import('@/pages/SignInPage.vue'),
    meta: { title: 'Sign in' },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/pages/ProfilePage.vue'),
    meta: { title: 'Profile' },
  },
  // A public per-user profile page, reached by clicking a unit's owner. The
  // `:login` is a single segment (it may be an email), matched separately from
  // the signed-in user's own `/profile` account page above.
  {
    path: '/users/:login',
    name: 'user-profile',
    component: () => import('@/pages/UserProfilePage.vue'),
    meta: { title: 'Profile' },
  },
  // "Manage users" moved under Settings; the old standalone route redirects so
  // existing links/bookmarks still land on the admin surface.
  { path: '/admin', redirect: { name: 'settings' } },
  {
    path: '/join',
    name: 'join',
    component: () => import('@/pages/JoinPage.vue'),
    meta: { title: 'Join' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/StubPage.vue'),
    meta: { title: 'Not found' },
  },
];

// The routes a signed-out visitor may reach: the sign-in page and the invite
// acceptance page (a joining user has no session yet). Everything else is
// private by default and redirects to sign-in.
const PUBLIC_ROUTES = new Set(['signin', 'join']);

/**
 * Register the private-by-default navigation guard. Exported so tests can apply
 * it to a memory router.
 *
 * A signed-out visitor is sent to `/signin` for every route but the public
 * sign-in/invite pages and the first-run Settings bootstrap; a signed-in
 * non-admin is kept off the admin-only Settings surface.
 *
 * SECURITY: this guard is UX-only — it spares a signed-out visitor a broken,
 * empty page rather than enforcing access. The server is the boundary: every
 * catalogue and admin endpoint answers a logged-out caller a 404 regardless of
 * what the client routes to.
 */
export function registerGuards(router: Router): void {
  router.beforeEach(async (to) => {
    const auth = useAuthStore();
    if (!auth.loaded) {
      await auth.fetchIdentity();
    }

    if (typeof to.name === 'string' && PUBLIC_ROUTES.has(to.name)) {
      return true;
    }

    // Settings keeps its own gate: an admin passes, and the unconfigured
    // first-run operator (no identity yet) may reach it to bootstrap a token; a
    // signed-in non-admin (member) is sent home.
    if (to.meta.requiresAdmin) {
      return auth.canAccessSettings ? true : { name: 'home' };
    }

    if (!auth.isAuthenticated) {
      return { name: 'signin' };
    }
    return true;
  });
}

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

registerGuards(router);

export default router;
